import { httpCoreService } from "../services/HttpCoreService";
import type { ApiResponse } from "../types/auth.type";
import type {
  RiskDashboardData,
  RiskListParams,
  RiskListResponse,
  CreateRiskRequest,
  CreateRiskResponse,
  UpdateRiskRequest,
  PatchRiskStatusRequest,
  ApiActivityEntry,
  RiskActivityEntry,
  ApiRisk,
} from "@/pages/IssueRiskTracker/types";
import type { Risk } from "@/pages/IssueRiskTracker/types";
import { fromApiRisk, fromApiActivity } from "@/pages/IssueRiskTracker/types";

// ─── Helper ──────────────────────────────────────────────────────────────────

function assertSuccess<T>(
  response: ApiResponse<T>,
  errorMsg: string,
): asserts response is ApiResponse<T> & { data: T } {
  const ok = response.statusCode >= 200 && response.statusCode < 300;
  if (!ok) {
    throw new Error(response.error ?? response.message ?? errorMsg);
  }
  if (!response.data) {
    throw new Error(`${errorMsg}: No data returned from server.`);
  }
}

// ─── Interface ───────────────────────────────────────────────────────────────

export interface IRiskRepository {
  getDashboard(studyId: string): Promise<RiskDashboardData>;
  getRisks(studyId: string, params?: RiskListParams): Promise<RiskListResponse>;
  getRiskById(studyId: string, riskId: string): Promise<Risk>;
  createRisk(
    studyId: string,
    data: CreateRiskRequest,
  ): Promise<CreateRiskResponse>;
  updateRisk(
    studyId: string,
    riskId: string,
    data: UpdateRiskRequest,
  ): Promise<void>;
  patchRiskStatus(
    studyId: string,
    riskId: string,
    data: PatchRiskStatusRequest,
  ): Promise<void>;
  getActivityLog(studyId: string, riskId: string): Promise<RiskActivityEntry[]>;
  deleteRisk(studyId: string, riskId: string): Promise<void>;
}

// ─── Implementation ──────────────────────────────────────────────────────────

export class RiskRepository implements IRiskRepository {
  async getDashboard(studyId: string): Promise<RiskDashboardData> {
    const response = await httpCoreService.get<RiskDashboardData>(
      `/studies/${studyId}/risks/dashboard`,
    );
    assertSuccess(response, "Failed to load risk dashboard");
    return response.data;
  }

  async getRisks(
    studyId: string,
    params?: RiskListParams,
  ): Promise<RiskListResponse> {
    const response = await httpCoreService.get<{
      items: ApiRisk[];
      total: number;
      page: number;
      limit: number;
    }>(`/studies/${studyId}/risks`, { params });
    assertSuccess(response, "Failed to load risks");
    return {
      ...response.data,
      items: response.data.items.map(fromApiRisk),
    };
  }

  async createRisk(
    studyId: string,
    data: CreateRiskRequest,
  ): Promise<CreateRiskResponse> {
    const response = await httpCoreService.post<CreateRiskResponse>(
      `/studies/${studyId}/risks`,
      data,
    );
    assertSuccess(response, "Failed to create risk");
    return response.data;
  }

  async getRiskById(studyId: string, riskId: string): Promise<Risk> {
    const response = await httpCoreService.get<ApiRisk>(
      `/studies/${studyId}/risks/${riskId}`,
    );
    assertSuccess(response, "Failed to load risk");
    return fromApiRisk(response.data);
  }

  async updateRisk(
    studyId: string,
    riskId: string,
    data: UpdateRiskRequest,
  ): Promise<void> {
    const response = await httpCoreService.put<null>(
      `/studies/${studyId}/risks/${riskId}`,
      data,
    );
    if (response.statusCode < 200 || response.statusCode >= 300) {
      throw new Error(
        response.error ?? response.message ?? "Failed to update risk",
      );
    }
  }

  async deleteRisk(studyId: string, riskId: string): Promise<void> {
    const response = await httpCoreService.delete<null>(
      `/studies/${studyId}/risks/${riskId}`,
    );
    if (response.statusCode < 200 || response.statusCode >= 300) {
      throw new Error(
        response.error ?? response.message ?? "Failed to delete risk",
      );
    }
  }

  async patchRiskStatus(
    studyId: string,
    riskId: string,
    data: PatchRiskStatusRequest,
  ): Promise<void> {
    const response = await httpCoreService.patch<null>(
      `/studies/${studyId}/risks/${riskId}/status`,
      data,
    );
    if (response.statusCode < 200 || response.statusCode >= 300) {
      throw new Error(
        response.error ?? response.message ?? "Failed to update risk status",
      );
    }
  }

  async getActivityLog(
    studyId: string,
    riskId: string,
  ): Promise<RiskActivityEntry[]> {
    const response = await httpCoreService.get<ApiActivityEntry[]>(
      `/studies/${studyId}/risks/${riskId}/activity`,
    );
    assertSuccess(response, "Failed to load activity log");
    return response.data.map(fromApiActivity);
  }
}
